import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "d-flex h-100 w-100 preview-wrapper" }
const _hoisted_2 = { class: "preview-main-full h-100" }
const _hoisted_3 = {
  key: 0,
  class: "revision-pane h-100"
}

import { ref, shallowRef, markRaw, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import MonacoLazyComponent from './MonacoLazyComponent.vue'
import PreviewComponent from './PreviewComponent.vue'
import EditorToolbarComponent from './EditorToolbarComponent.vue'
import NavigatorComponent from './NavigatorComponent.vue'
import { Splitpanes, Pane } from 'splitpanes'
import 'splitpanes/dist/splitpanes.css'
import ComparisonComponent from './ComparisonComponent.vue'
import RevisionComponent from './RevisionComponent.vue'
import AiSidebarChatComponent from './AiSidebarChatComponent.vue'
import { qEditorState } from '../stores'
import MathLive from './MathLiveComponent.vue'
import { editorEventBus } from '../stores/eventBus'

// Template refs

export default /*@__PURE__*/_defineComponent({
  __name: 'QuestionEditorComponent',
  setup(__props) {

const monacoComponent = ref<any>(null)
const sidebarChat = ref<any>(null)

// Feature flag: Enable AI inline edit (Cmd+K) in Monaco Editor
// Set via data attribute: <div id="question_editor_data" data-use-monaco-editor="true">
const enableAiInlineEdit = ref(false)

// AI Sidebar Chat state
const sidebarChatVisible = ref(false)
// Monaco editor instance must NOT be deep-reactive: Vue 3's reactive Proxy would
// wrap the huge editor object and cause infinite update loops (UI freeze) when its
// methods are read inside computeds. shallowRef + markRaw keeps it raw.
const monacoEditor = shallowRef<any>(null)

const leftPaneSize = ref(50)

const exam = computed(() => qEditorState.exam)
const examId = computed(() => exam.value?.id || 0)
const isEditorMode = computed(() => !qEditorState.isRevisionMode)
const isRevisionMode = computed(() => qEditorState.isRevisionMode)

// Cmd+Shift+L (Mac) or Ctrl+Shift+L (Windows): Cursor-like flow
//   From editor: capture selection context → open sidebar → focus chat input
//   From chat: focus back to editor (so user can make new selection)
const onGlobalKeydown = (e: KeyboardEvent) => {
  if (e.isComposing) return

  if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.code === 'KeyL') {
    e.preventDefault()
    if (!enableAiInlineEdit.value) return

    const chat = sidebarChat.value

    // If focus is currently in chat input → go back to editor
    if (sidebarChatVisible.value && chat) {
      const chatInput = (chat.inputField ?? chat.$refs?.inputField) as HTMLTextAreaElement
      if (chatInput && document.activeElement === chatInput) {
        if (monacoEditor.value?.focus) monacoEditor.value.focus()
        return
      }
    }

    // From editor: capture context, open sidebar if needed, focus chat
    if (!sidebarChatVisible.value) {
      sidebarChatVisible.value = true
      editorEventBus.emit('sidebar-chat-changed', sidebarChatVisible.value)
    }

    nextTick(() => {
      if (chat) {
        if (chat.captureEditorContext) chat.captureEditorContext()
        if (chat.openWithPrompt) chat.openWithPrompt()
      }
    })
  }
}

const onEditorReady = (editor: any) => {
  monacoEditor.value = markRaw(editor)

  // Register Cmd+Shift+L action in Monaco editor too
  if (editor && typeof editor.addAction === 'function') {
    editor.addAction({
      id: 'ai-sidebar-chat-toggle',
      label: 'Toggle AI Sidebar Chat',
      keybindings: [
        // CtrlCmd + Shift + L
        2048 | 1024 | 42
      ],
      run: () => {
        onGlobalKeydown(new KeyboardEvent('keydown', {
          code: 'KeyL', metaKey: true, shiftKey: true
        }))
      }
    })
  }
}

const toggleSidebarChat = () => {
  if (!enableAiInlineEdit.value) return
  sidebarChatVisible.value = !sidebarChatVisible.value
  // Notify toolbar about the state change
  editorEventBus.emit('sidebar-chat-changed', sidebarChatVisible.value)
  // When opening, ensure Chat tab is active and focused
  if (sidebarChatVisible.value) {
    nextTick(() => {
      const chat = sidebarChat.value
      if (chat?.openWithPrompt) chat.openWithPrompt()
    })
  }
}

const closeSidebarChat = () => {
  sidebarChatVisible.value = false
  editorEventBus.emit('sidebar-chat-changed', false)
}

const onAiContentChange = (content: string) => {
  // When AI applies changes, sync the editor content to the store
  qEditorState.editBody({ body: content })
}

const onToggleAiChat = () => {
  toggleSidebarChat()
}

onMounted(() => {
  // Check feature flag for AI inline edit (Cmd+K)
  const editorData = document.getElementById('question_editor_data')
  enableAiInlineEdit.value = editorData?.dataset.useMonacoEditor === 'true'

  qEditorState.loadData()

  // Load saved pane size from localStorage
  const savedSize = localStorage.getItem('qeditor_pane_size')
  if (savedSize) {
    const parsedSize = parseFloat(savedSize)
    // Validate: must be between 5 and 95
    if (parsedSize >= 5 && parsedSize <= 95) {
      leftPaneSize.value = parsedSize
    } else {
      // Invalid value, reset to 50
      leftPaneSize.value = 50
      localStorage.setItem('qeditor_pane_size', '50')
    }
  }

  // Listen for global keyboard shortcut to toggle sidebar chat
  document.addEventListener('keydown', onGlobalKeydown)
  // Listen for toolbar toggle-ai-chat event
  editorEventBus.on('toggle-ai-chat', onToggleAiChat)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onGlobalKeydown)
  editorEventBus.off('toggle-ai-chat', onToggleAiChat)
})

const onPaneResized = (panes: any) => {
  try {
    if (panes?.panes?.length > 0) {
      leftPaneSize.value = panes.panes[0].size
      localStorage.setItem('qeditor_pane_size', String(panes.panes[0].size))
    } else if (Array.isArray(panes) && panes.length > 0) {
      leftPaneSize.value = panes[0].size
      localStorage.setItem('qeditor_pane_size', String(panes[0].size))
    }
  } catch (e) {
    console.error('[QuestionEditorComponent] onPaneResized error:', e)
  }
}

const handleKeydown = (event: KeyboardEvent) => {
  const isMac = window.navigator.platform.toUpperCase().indexOf('MAC') >= 0
  const saveKey = isMac ? event.metaKey : event.ctrlKey

  if (saveKey && event.key === 's') {
    event.preventDefault()
    save()
  }
}

const save = () => {
  if (isEditorMode.value) {
    qEditorState.saveQuestions()
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    id: "question_editor",
    class: "v-application mx-n3",
    onKeydown: handleKeydown,
    tabindex: "0",
    row: "",
    wrap: ""
  }, [
    _createVNode(MathLive),
    _createVNode(_unref(Splitpanes), {
      class: "default-theme editor-splitpanes",
      onResized: onPaneResized
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(Pane), {
          size: leftPaneSize.value,
          "min-size": 5,
          class: "editor d-flex flex-column justify-lg-space-between"
        }, {
          default: _withCtx(() => [
            _createVNode(EditorToolbarComponent),
            _createCommentVNode(" Monaco Editor (always loaded) "),
            _createVNode(MonacoLazyComponent, {
              ref_key: "monacoComponent",
              ref: monacoComponent,
              "editor-id": "question_monaco_editor",
              "enable-ai-inline-edit": enableAiInlineEdit.value,
              onEditorReady: onEditorReady
            }, null, 8 /* PROPS */, ["enable-ai-inline-edit"])
          ]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["size"]),
        _createVNode(_unref(Pane), {
          size: 100 - leftPaneSize.value,
          "min-size": 5,
          class: "preview"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createCommentVNode(" Normal mode: Preview (and Revision) "),
              (!sidebarChatVisible.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createElementVNode("div", _hoisted_2, [
                      _createVNode(NavigatorComponent),
                      _createVNode(PreviewComponent)
                    ]),
                    (isRevisionMode.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                          _createVNode(RevisionComponent)
                        ]))
                      : _createCommentVNode("v-if", true)
                  ], 64 /* STABLE_FRAGMENT */))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createCommentVNode(" AI Chat mode: Chat + Preview tabs "),
                    _createVNode(AiSidebarChatComponent, {
                      ref_key: "sidebarChat",
                      ref: sidebarChat,
                      visible: sidebarChatVisible.value,
                      editor: monacoEditor.value,
                      "exam-id": examId.value,
                      "on-content-change": onAiContentChange,
                      onClose: closeSidebarChat
                    }, {
                      preview: _withCtx(() => [
                        _createVNode(NavigatorComponent),
                        _createVNode(PreviewComponent)
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["visible", "editor", "exam-id"])
                  ], 64 /* STABLE_FRAGMENT */))
            ])
          ]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["size"])
      ]),
      _: 1 /* STABLE */
    }),
    _createCommentVNode(" 一番やりたいことはQeditorで全部出来るこなので、一旦非表示します "),
    _createCommentVNode(" <SidebarMenuComponent></SidebarMenuComponent> "),
    _createVNode(ComparisonComponent)
  ], 32 /* NEED_HYDRATION */))
}
}

})